import React, { useState, useEffect } from "react";
import moment from "moment";
import { useSubscription } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import IconButton from "@material-ui/core/IconButton";

import StatusBadge from "designV2/components/StatusBadge";
import "./orderModals.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const UPDATE_ORDERS_SUBSCRIPTION = gql`
  subscription updateOrder($orderId: ID!) {
    updateOrder(orderId: $orderId) {
      _id
      user {
        _id
      }
      shipmentModel
      type
      finalPrice
      commission
      createdAt
      finished
      status
      rejectedFor
      trip {
        _id
        trackId
        origin {
          coordinates
        }
      }
      tracking {
        trackId
      }
      cart {
        products {
          quantity
          product {
            _id
            title
          }
        }
      }
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const STATUS_TONES = {
  WAITING: "neutral",
  PAID: "info",
  DELIVERY_NOT_ACCEPTED: "warning",
  PREPARING: "info",
  PREPARED: "info",
  STOCK: "info",
  SHIPPING: "info",
  DELIVERED: "success",
  REJECTED: "danger",
};

// Matches OrdersHistory's own copy of this map (used for the row badge) —
// duplicated here rather than imported since OrderDetails is a standalone
// shared component used from four different screens.
const REJECTION_LABELS = {
  packingDamaged: "Packing damaged",
  damagedForPacking: "Damaged in packing",
  differentProduct: "Wrong product",
  noReceiver: "No receiver",
  canceledByShopAdmin: "Cancelled by shop",
  deliveryWasNotAccepted: "Delivery not accepted",
};

const SHIPMENT_LABELS = {
  SPARK: "Spark delivery",
  SHOP: "Delivered by shop",
};

const statusLabel = (status) => (status ? status.replace(/_/g, " ").toLowerCase() : "-");

/**
 * OrderDetails - read-only order summary. Content-only (no title, no
 * close button, no Dialog wrapper of its own) — every call site renders
 * it inside a shared DialogShell so the chrome, backdrop and close
 * button are consistent with the rest of the app instead of each place
 * that shows an order re-implementing them.
 *
 * The two status-change mutations this file used to import
 * (changeOrderStatusToPreparingByShopAdmin / …ToShipping) were declared
 * with working handlers but never actually had a button wired to call
 * them — dead code, not a feature. Advancing an order's status already
 * has a complete, better flow in DeliveryStatus.js (reasons, receiver
 * info, Spark delivery requests), so this view stays purely
 * informational rather than offering a second, more limited way to do
 * the same thing.
 */
const OrderDetails = ({ order: item }) => {
  const [order, setOrder] = useState(item);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setOrder(item);
  }, [item]);

  const { data: subUpdateData } = useSubscription(UPDATE_ORDERS_SUBSCRIPTION, {
    variables: { orderId: order._id },
  });

  useEffect(() => {
    if (subUpdateData) setOrder(subUpdateData.updateOrder);
  }, [subUpdateData]);

  const trackId = order.tracking && order.tracking.trackId;

  const handleCopy = () => {
    if (!trackId || !navigator.clipboard) return;
    navigator.clipboard.writeText(trackId).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="orderDetailsBody">
      <div className="orderDetailsTopRow">
        <StatusBadge status={STATUS_TONES[order.status] || "neutral"} label={statusLabel(order.status)} />
        <span className="orderDetailsAmount">${(order.finalPrice || 0).toFixed(2)}</span>
      </div>

      {order.status === "REJECTED" && order.rejectedFor && (
        <div className="orderDetailsRejection">
          <span className="orderDetailsLabel">Rejected because</span>
          <span>{REJECTION_LABELS[order.rejectedFor] || order.rejectedFor}</span>
        </div>
      )}

      <div className="orderDetailsGrid">
        {trackId && (
          <div className="orderDetailsField orderDetailsFieldWide">
            <span className="orderDetailsLabel">Tracking ID</span>
            <span className="orderDetailsValueRow">
              <span className="orderDetailsMono">{trackId}</span>
              <IconButton size="small" className="orderModalIconButton" onClick={handleCopy} title="Copy">
                {copied ? <CheckIcon fontSize="small" /> : <FileCopyIcon style={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </div>
        )}

        <div className="orderDetailsField">
          <span className="orderDetailsLabel">Customer</span>
          <span className="orderDetailsValue">{(order.user && order.user.fullName) || "-"}</span>
          {order.user && order.user.phoneNumber && (
            <span className="orderDetailsSubValue">{order.user.phoneNumber}</span>
          )}
        </div>

        <div className="orderDetailsField">
          <span className="orderDetailsLabel">Created</span>
          <span className="orderDetailsValue">{moment(order.createdAt).format("MMM D, YYYY hh:mm A")}</span>
        </div>

        {order.shipmentModel && (
          <div className="orderDetailsField">
            <span className="orderDetailsLabel">Delivery</span>
            <span className="orderDetailsValue">{SHIPMENT_LABELS[order.shipmentModel] || order.shipmentModel}</span>
          </div>
        )}
      </div>

      <div className="orderDetailsField">
        <span className="orderDetailsLabel">Products</span>
        <div className="orderDetailsProducts">
          {order.cart && order.cart.products && order.cart.products.length > 0 ? (
            order.cart.products.map(
              (item) =>
                item &&
                item.product && (
                  <span key={item.product._id} className="orderDetailsProductTag">
                    {item.quantity}&times; {item.product.title}
                  </span>
                )
            )
          ) : (
            <span className="orderDetailsMuted">No products on this order</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
